/*
 * Sidebar 컴포넌트
 *
 * ⚠️  메뉴별 임시 접근 제한 적용됨 ⚠️
 *
 * 현재 일부 메뉴는 순차 오픈을 위해 특정 권한의 사용자만 표시되도록 제한되어 있습니다.
 *
 * 제한 조건:
 * - userLevel 4 이하: 모든 메뉴 표시
 * - userLevel 5 이상: 제한된 메뉴는 사이드바에서 숨김
 *
 * 제한 대상 메뉴 (각각 독립적으로 제어 가능):
 * - 시설물 예약: RESTRICT_FACILITY_MENU
 * - 화물차 주차장 정기권 신청: RESTRICT_PARKING_MENU
 * - 포트빌 관리비 청구서: RESTRICT_PORTVILLE_BILLING_MENU
 *
 * 정식 오픈 시 해제 방법:
 * 1. 해당 메뉴의 접근 제한 상수를 false로 변경 (예: RESTRICT_FACILITY_MENU = false)
 * 2. 모든 메뉴 오픈 후 관련 주석 및 코드 블록 제거
 *
 * 검색 키워드: "메뉴 접근 제한", "순차 오픈용", "RESTRICT_"
 */

import React, { memo, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';

// ===== 메뉴별 접근 제한 설정 (순차 오픈용) =====
// TODO: 사이트 정식 오픈 시 각 플래그를 false로 변경하여 접근 제한을 해제하세요
const RESTRICT_FACILITY_MENU = true;           // 시설물 예약 메뉴 제한
const RESTRICT_PARKING_MENU = true;            // 화물차 주차장 정기권 신청 메뉴 제한
const RESTRICT_PORTVILLE_BILLING_MENU = true;  // 포트빌 관리비 청구서 메뉴 제한
// 허용되는 최대 userLevel (4 이하만 메뉴 표시, 5 이상은 제한)
const MAX_ALLOWED_USER_LEVEL = 4;
// ============================================

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isMobile: boolean;
  location: { pathname: string };
}

// 메모이제이션을 통한 불필요한 리렌더링 방지
const Sidebar: React.FC<SidebarProps> = memo(({ isOpen, onClose, isMobile, location }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const auth = getAuth();
  const { user } = useAuth();
  const [userLevel, setUserLevel] = useState<number | null>(null);

  // 사용자 권한 레벨 조회
  useEffect(() => {
    const fetchUserLevel = async () => {
      if (!user) {
        setUserLevel(null);
        return;
      }

      try {
        const userProfileRef = doc(db, 'user_profiles', user.uid);
        const userProfileSnap = await getDoc(userProfileRef);

        if (userProfileSnap.exists()) {
          const userProfileData = userProfileSnap.data();
          setUserLevel(userProfileData.userLevel ?? 999);
        } else {
          setUserLevel(999); // 프로필 없으면 최고 제한
        }
      } catch (error) {
        console.error('사용자 권한 조회 실패:', error);
        setUserLevel(999);
      }
    };

    fetchUserLevel();
  }, [user]);

  // 공개 메뉴 (항상 표시, 권한 확인 불필요)
  const publicMenuItems = [
    { path: '/', label: '홈' },
    { path: '/portville', label: '포트빌 입주 신청' },
    { path: '/facility', label: '시설물 예약' },
    { path: '/inquiry', label: '고객센터' },
    { path: '/settings', label: '설정' },
  ];

  // 제한 메뉴 (권한 확인 필요)
  const restrictedMenuItems = [
    {
      path: '/portville/billing',
      label: '포트빌 관리비 청구서',
      restrictFlag: RESTRICT_PORTVILLE_BILLING_MENU,
      insertAfter: '/portville' // 포트빌 입주 신청 다음에 삽입
    },
    {
      path: '/parking',
      label: '화물차 주차장 정기권 신청',
      restrictFlag: RESTRICT_PARKING_MENU,
      insertAfter: '/portville/billing'
    },
    {
      path: '/facility',
      label: '시설물 예약',
      restrictFlag: RESTRICT_FACILITY_MENU,
      insertAfter: '/parking'
    },
  ];

  // 표시할 메뉴 계산
  const visibleMenuItems = (() => {
    // 기본: 공개 메뉴만 표시
    const result = [...publicMenuItems];

    // 권한이 있는 경우 제한 메뉴 추가
    if (userLevel !== null && userLevel <= MAX_ALLOWED_USER_LEVEL) {
      // 활성화된 제한 메뉴만 필터링
      const activeRestrictedMenus = restrictedMenuItems.filter(item => !item.restrictFlag);

      // 포트빌 입주 신청 다음에 제한 메뉴들을 순서대로 삽입
      const portvilleIndex = result.findIndex(item => item.path === '/portville');
      if (portvilleIndex !== -1) {
        result.splice(portvilleIndex + 1, 0, ...activeRestrictedMenus);
      }
    }

    return result;
  })();

  const handleLogout = async () => {
    try {
      // Firebase 로그아웃
      await signOut(auth);
      
      // 로컬 스토리지의 토큰 제거
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      
      // 세션 스토리지의 사용자 정보 제거
      sessionStorage.removeItem('user');
      
      // 인덱스 페이지로 리다이렉트
      navigate('/');
    } catch (error) {
      console.error('로그아웃 중 오류 발생:', error);
    }
  };

  return (
    <aside
      className={`fixed lg:static w-64 bg-white shadow-lg h-full z-30 transform will-change-transform ${
        isMobile && !isOpen ? '-translate-x-full' : 'translate-x-0'
      }`}
      style={{ 
        transition: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1)',
        contain: 'layout style paint',  // 성능 최적화를 위한 CSS contain 속성
        overscrollBehavior: 'contain',  // 스크롤 동작 개선
      }}
      aria-hidden={isMobile && !isOpen}
    >
      <div className="p-4 flex justify-between items-center">
        <h1 className="text-xl font-bold text-gray-800">부산항신항시설관리센터</h1>
        {isMobile && (
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 p-2 -mr-2 touch-manipulation"
            aria-label="닫기"
            type="button"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
      <nav className="mt-6 overflow-y-auto overscroll-contain" style={{ maxHeight: 'calc(100vh - 80px)' }}>
        {visibleMenuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={isMobile ? onClose : undefined}
            className={`relative flex items-center h-[54px] px-5 text-gray-700 touch-manipulation ${
              ((item.path === '/portville' && location.pathname === '/portville') ||
               (item.path === '/portville/billing' && location.pathname.startsWith('/portville/billing')) ||
               (item.path === '/parking' && location.pathname.startsWith('/parking')) ||
               (item.path === '/facility' && location.pathname.startsWith('/facility')) ||
               (item.path === '/inquiry' && location.pathname.startsWith('/inquiry')) ||
               (item.path !== '/portville' && item.path !== '/portville/billing' && item.path !== '/parking' && item.path !== '/facility' && item.path !== '/inquiry' && location.pathname === item.path))
                ? 'bg-[#4b8fe4] text-white font-bold text-lg'
                : 'text-lg font-bold hover:bg-gray-100'
            }`}
            prefetch="intent"
            tabIndex={isMobile && !isOpen ? -1 : 0}
          >
            <span className="font-['Pretendard']">{item.label}</span>
            {((item.path === '/portville' && location.pathname.startsWith('/portville')) ||
              (item.path === '/parking' && location.pathname.startsWith('/parking')) ||
              (item.path === '/facility' && location.pathname.startsWith('/facility')) ||
              (item.path === '/inquiry' && location.pathname.startsWith('/inquiry')) ||
              (item.path !== '/portville' && item.path !== '/parking' && item.path !== '/facility' && item.path !== '/inquiry' && location.pathname === item.path)) && (
              <div className="absolute right-5 w-6 h-6">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 12H16M16 12L12 8M16 12L12 16"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            )}
          </Link>
        ))}
        
        <div className="border-t border-gray-200 my-2"></div>
        
        <button
          onClick={handleLogout}
          className="relative flex items-center h-[54px] px-5 hover:bg-gray-100 touch-manipulation w-full text-lg font-bold text-gray-700"
          tabIndex={isMobile && !isOpen ? -1 : 0}
        >
          <span className="font-['Pretendard']">로그아웃</span>
        </button>
      </nav>
    </aside>
  );
});

// 명시적인 컴포넌트 이름 지정 (디버깅 목적)
Sidebar.displayName = 'Sidebar';

export default Sidebar; 